import { api } from "../api.js";
import { replaceQuery } from "../router.js";
import {
  agreeMark,
  escapeHtml,
  fmtDateTime,
  housePill,
  memberUrl,
  propositionLabel,
  propositionUrl,
  resultLabel,
  truncate,
  voteLabel,
  votePill,
} from "../format.js";
import { renderCountsBar } from "../components/charts.js";
import { renderSegmented, bindSegmented } from "../components/segmented.js";
import { renderOrientations, flattenGroups, renderVoteTable, bindVoteTable } from "../components/votes.js";

const VIEWS = [
  { value: "party", label: "Por partido" },
  { value: "list", label: "Lista alfabética" },
];

export async function renderRollCall({ root, match, query, isCurrent }) {
  const id = match[1];
  const r = await api.rollCall(id);
  if (!isCurrent()) return;
  const res = resultLabel(r.result);
  const p = r.proposition;
  document.title = `Votação ${p ? propositionLabel(p) : r.external_id} · Como Vota`;
  const state = { view: query.view === "list" ? "list" : "party" };

  const orientations = (r.orientations || []).filter((o) => o.orientation);
  const govOfficial = orientations.some((o) => o.bancada_kind === "governo" && o.source === "official");
  const hasVotes = Boolean(r.groups?.length);

  root.innerHTML = `
    <article class="detail detail--wide">
      ${p ? `<a class="back-link" href="${propositionUrl(p)}" data-link>← ${escapeHtml(propositionLabel(p))}</a>` : `<a class="back-link" href="/proposicoes" data-link>← Proposições</a>`}
      <header class="detail__head">
        <div class="detail__meta">
          ${housePill(r.house)}
          ${r.session_house && r.session_house !== "SF" && r.session_house !== "CD" ? `<span class="pill pill--neutral">${escapeHtml(r.session_house)}</span>` : ""}
          ${r.organ_sigla ? `<span class="meta-badge">${escapeHtml(r.organ_sigla)}</span>` : ""}
          <span class="meta-muted">${fmtDateTime(r.happened_at)}</span>
          <span class="pill pill--${res.variant}">${escapeHtml(res.text)}</span>
          ${r.is_secret ? `<span class="pill pill--neutral">votação secreta</span>` : ""}
          ${!r.is_nominal ? `<span class="pill pill--neutral">simbólica</span>` : ""}
        </div>
        <h1 class="detail__title">${escapeHtml(truncate(r.description, 240)) || "Votação"}</h1>
        ${p ? `<p class="status"><a class="inline-link" href="${propositionUrl(p)}" data-link>${escapeHtml(propositionLabel(p))}</a>${p.summary_one_line ? ` · <span class="muted">${escapeHtml(p.summary_one_line)}</span>` : p.ementa ? ` · <span class="muted">${escapeHtml(truncate(p.ementa, 200))}</span>` : ""}</p>` : ""}
        ${r.subject ? `<p class="muted">${escapeHtml(r.subject)}</p>` : ""}
      </header>

      <section class="detail__section">
        <h2 class="section-title">Resultado</h2>
        ${renderCountsBar(r)}
      </section>

      <section class="detail__section">
        <h2 class="section-title">Orientações</h2>
        ${orientations.length ? renderOrientations(orientations, govOfficial) : `<p class="muted">Sem orientações registradas para esta votação.</p>`}
      </section>

      <section class="detail__section">
        <div class="section-head">
          <h2 class="section-title">Votos nominais</h2>
          ${hasVotes ? renderSegmented(VIEWS, state.view, { attr: "view", label: "Modo de exibição", size: "sm" }) : ""}
        </div>
        ${r.is_secret ? `<p class="muted">Votação secreta: os votos individuais não são divulgados.</p>` : ""}
        <div data-votes-body></div>
      </section>
    </article>`;

  const body = root.querySelector("[data-votes-body]");
  function renderBody() {
    if (!hasVotes) {
      body.innerHTML = r.is_secret ? "" : `<p class="muted">Votos individuais não disponíveis.</p>`;
      return;
    }
    if (state.view === "list") {
      const rows = flattenGroups(r.groups);
      body.innerHTML = renderVoteTable(rows);
      bindVoteTable(body, rows);
    } else {
      body.innerHTML = renderGroups(r.groups);
    }
  }
  bindSegmented(root, "view", (view) => {
    state.view = view;
    replaceQuery({ view: view === "list" ? "list" : "" });
    renderBody();
  });
  renderBody();
}

function renderGroups(groups) {
  const sorted = [...groups].sort((a, b) => sumCounts(b.counts) - sumCounts(a.counts));
  return `<div class="groups">${sorted
    .map((g, i) => {
      const counts = Object.entries(g.counts || {})
        .filter(([, n]) => n)
        .sort((a, b) => b[1] - a[1])
        .map(([k, n]) => `<span class="total-pill total-pill--${escapeHtml(k)}"><strong>${n}</strong> ${escapeHtml(voteLabel(k).toLowerCase())}</span>`)
        .join("");
      return `<details class="group" ${i < 3 ? "open" : ""}>
        <summary>
          <span class="group__party">${escapeHtml(g.party_sigla || "Sem partido")}</span>
          ${g.orientation ? `<span class="meta-muted">orientação</span> ${votePill(g.orientation, "pill--sm")}` : ""}
          <span class="totals totals--inline">${counts}</span>
        </summary>
        <ul class="votes-grid">${(g.votes || [])
          .map(
            (v) => `<li class="vote-item">
              <a href="${memberUrl(v.member)}" data-link>${v.member.photo_url ? `<img class="avatar avatar--sm" src="${escapeHtml(v.member.photo_url)}" alt="" loading="lazy" />` : ""}${escapeHtml(v.member.name)}</a>
              <span class="meta-muted">${escapeHtml(v.member.uf || "")}</span>
              ${votePill(v.vote, "pill--sm")}
              <span class="marks" title="partido / governo">${agreeMark(v.agrees_party_orientation)}${agreeMark(v.agrees_gov)}</span>
            </li>`,
          )
          .join("")}</ul>
      </details>`;
    })
    .join("")}</div>`;
}

function sumCounts(counts) {
  return Object.values(counts || {}).reduce((s, n) => s + Number(n || 0), 0);
}
